import React, { useCallback, useEffect, useState } from "react"
import toast from "react-hot-toast"
import { FaWarehouse, FaStream } from "react-icons/fa"
import api from "../../libs/axios"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import {
  apiError,
  dateTimeText,
  fetchPaged,
  FilterOption,
  fullName,
  HolderType,
  materialFilters,
  qty,
  stripEmpty,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  FilterField,
  FilterRow,
  HolderChip,
  ListCard,
  RefreshButton,
  ScopeNote,
  Select,
  TextInput,
  Th,
  ViewToggle,
} from "./shared/MaterialUI"

/**
 * Live stock and the movement history behind it.
 *
 * Two views over the same data:
 *   Stock    what each holder has right now, either per holder or rolled up
 *            per item across the warehouse, staff and sites.
 *   Ledger   every movement, with its flow bucket, so an unexpected balance
 *            can be traced back to the document that caused it.
 */

const FLOW_LABEL: Record<string, string> = {
  inflow: "Inflow",
  internal_outflow: "Issued out",
  internal_inflow: "Received in",
  outflow: "Outflow",
}

const FLOW_TONE: Record<string, string> = {
  inflow: "bg-green-100 text-green-800",
  internal_outflow: "bg-amber-100 text-amber-800",
  internal_inflow: "bg-sky-100 text-sky-800",
  outflow: "bg-red-100 text-red-800",
}

type StockView = "stock" | "ledger"

const VIEWS = [
  { key: "stock", label: "Stock balances", icon: <FaWarehouse className="w-4 h-4" /> },
  { key: "ledger", label: "Movement ledger", icon: <FaStream className="w-4 h-4" /> },
]

const MaterialStockPage: React.FC = () => {
  const [view, setView] = useState<StockView>("stock")
  // Each view is its own card with its own filters; the switch sits in the
  // card header, as the List / Summary switch does on Project Sites.
  const toggle = <ViewToggle options={VIEWS} value={view} onChange={(k) => setView(k as StockView)} />
  return (
    <div className="space-y-4">
      {view === "stock" ? <StockBalances toggle={toggle} /> : <MovementLedger toggle={toggle} />}
    </div>
  )
}

const StockBalances: React.FC<{ toggle: React.ReactNode }> = ({ toggle }) => {
  const [groupBy, setGroupBy] = useState<"holder" | "item">("item")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [item, setItem] = useState<FilterOption | null>(null)
  const [holderType, setHolderType] = useState<"" | HolderType>("")
  const [hideZero, setHideZero] = useState(true)
  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(25)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = stripEmpty({
        group_by: groupBy === "item" ? "item" : undefined,
        project_id: project?.id,
        item_id: item?.id,
        holder_type: holderType || undefined,
        hide_zero: hideZero ? "true" : undefined,
        page,
        limit,
      })
      if (groupBy === "item") {
        const res = await api.get("/materials/inventory/balances", { params })
        setRows(res.data?.data?.rows || [])
        setPagination(null)
      } else {
        const result = await fetchPaged("/materials/inventory/balances", params)
        setRows(result.rows)
        setPagination(result.pagination)
      }
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load stock balances"))
    } finally {
      setLoading(false)
    }
  }, [groupBy, project, item, holderType, hideZero, page, limit])

  useEffect(() => {
    load()
  }, [load])

  return (
    <ListCard
      icon={<FaWarehouse className="w-5 h-5" />}
      title="Stock & Ledger"
      subtitle="Where the company's material is right now. Material issued to staff or a site is still in stock - it has only changed hands."
      actions={
        <>
          {toggle}
          <RefreshButton loading={loading} onClick={load} />
        </>
      }
      filters={
        <FilterRow>
          <FilterField label="View">
            <Select
              value={groupBy}
              onChange={(e) => {
                setGroupBy(e.target.value as any)
                setPage(1)
              }}
            >
              <option value="item">Per item (rolled up)</option>
              <option value="holder">Per holder</option>
            </Select>
          </FilterField>
          <FilterField label="Project">
            <AsyncPicker
              value={project}
              onChange={(o) => {
                setProject(o)
                setPage(1)
              }}
              fetcher={(s) => materialFilters.projects(s)}
              placeholder="All my projects"
              emptyHint="You are not assigned to any project"
            />
          </FilterField>
          <FilterField label="Item">
            <AsyncPicker
              value={item}
              onChange={(o) => {
                setItem(o)
                setPage(1)
              }}
              fetcher={(s) => materialFilters.items(project ? String(project.id) : undefined, s)}
              dependency={project?.id}
              placeholder="All items"
            />
          </FilterField>
          {groupBy === "holder" ? (
            <FilterField label="Holder Type">
              <Select
                value={holderType}
                onChange={(e) => {
                  setHolderType(e.target.value as any)
                  setPage(1)
                }}
              >
                <option value="">All holders</option>
                <option value="warehouse">Warehouse</option>
                <option value="staff">Staff</option>
                <option value="site">Site</option>
              </Select>
            </FilterField>
          ) : null}
          <FilterField label="&nbsp;" className="lg:w-auto">
            <label className="flex items-center gap-2 text-sm text-gray-700 h-[38px]">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
              />
              Hide zero balances
            </label>
          </FilterField>
        </FilterRow>
      }
      footer={
        pagination ? (
          <Pagination
            pagination={pagination}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l)
              setPage(1)
            }}
          />
        ) : null
      }
    >
      {groupBy === "item" ? (
        <DataTable
          colSpan={6}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading stock..."
          emptyText="No stock found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaWarehouse />}
          head={
            <>
              <Th>Item</Th>
              <Th>Unit</Th>
              <Th className="text-right">In Warehouse</Th>
              <Th className="text-right">With Staff</Th>
              <Th className="text-right">At Site</Th>
              <Th className="text-right">Total</Th>
            </>
          }
        >
          {rows.map((row) => (
            <tr key={row.item_id} className="hover:bg-gray-50 transition-colors">
              <Cell>
                <span className="font-medium">{row.item_name}</span>
                <span className="text-xs text-gray-400"> ({row.item_code})</span>
                {row.billable ? (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">billable</span>
                ) : null}
              </Cell>
              <Cell className="text-gray-500">{row.unit || "-"}</Cell>
              <Cell className="text-right">{qty(row.in_warehouse)}</Cell>
              <Cell className="text-right text-amber-700">{qty(row.with_staff)}</Cell>
              <Cell className="text-right text-sky-700">{qty(row.at_site)}</Cell>
              <Cell className="text-right font-medium">{qty(row.total_quantity)}</Cell>
            </tr>
          ))}
        </DataTable>
      ) : (
        <DataTable
          colSpan={6}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading stock..."
          emptyText="No stock found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaWarehouse />}
          head={
            <>
              <Th>Holder</Th>
              <Th>Item</Th>
              <Th>Project</Th>
              <Th className="text-right">On Hand</Th>
              <Th className="text-right">Reserved</Th>
              <Th className="text-right">Available</Th>
            </>
          }
        >
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50 transition-colors">
              <Cell>
                <HolderChip type={row.holder_type} label={row.holder_label} code={row.holder_code} />
              </Cell>
              <Cell>
                {row.item?.item_name || `#${row.item_id}`}
                {row.item?.item_code ? <span className="text-xs text-gray-400"> ({row.item.item_code})</span> : null}
              </Cell>
              <Cell className="text-gray-500">
                {row.project?.project_short_name || row.project?.project_name || "Unallocated"}
              </Cell>
              <Cell className="text-right">{qty(row.quantity_on_hand)}</Cell>
              <Cell className="text-right text-gray-500">{qty(row.reserved_quantity)}</Cell>
              <Cell className="text-right font-medium">{qty(row.available_quantity)}</Cell>
            </tr>
          ))}
        </DataTable>
      )}
    </ListCard>
  )
}

const MovementLedger: React.FC<{ toggle: React.ReactNode }> = ({ toggle }) => {
  const [project, setProject] = useState<FilterOption | null>(null)
  const [item, setItem] = useState<FilterOption | null>(null)
  const [flowCategory, setFlowCategory] = useState("")
  const [movementType, setMovementType] = useState("")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(25)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchPaged("/materials/inventory/ledger", {
        page,
        limit,
        project_id: project?.id,
        item_id: item?.id,
        flow_category: flowCategory || undefined,
        movement_type: movementType || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      })
      setRows(result.rows)
      setPagination(result.pagination)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load the ledger"))
    } finally {
      setLoading(false)
    }
  }, [page, limit, project, item, flowCategory, movementType, fromDate, toDate])

  useEffect(() => {
    load()
  }, [load])

  return (
    <ListCard
      icon={<FaStream className="w-5 h-5" />}
      title="Stock & Ledger"
      subtitle="Every movement, with its flow bucket, so an unexpected balance can be traced back to the document that caused it."
      actions={
        <>
          {toggle}
          <RefreshButton loading={loading} onClick={load} />
        </>
      }
      filters={
        <FilterRow>
          <FilterField label="Project">
            <AsyncPicker
              value={project}
              onChange={(o) => {
                setProject(o)
                setPage(1)
              }}
              fetcher={(s) => materialFilters.projects(s)}
              placeholder="All my projects"
            />
          </FilterField>
          <FilterField label="Item">
            <AsyncPicker
              value={item}
              onChange={(o) => {
                setItem(o)
                setPage(1)
              }}
              fetcher={(s) => materialFilters.items(project ? String(project.id) : undefined, s)}
              dependency={project?.id}
              placeholder="All items"
            />
          </FilterField>
          <FilterField label="Flow">
            <Select
              value={flowCategory}
              onChange={(e) => {
                setFlowCategory(e.target.value)
                setPage(1)
              }}
            >
              <option value="">All flows</option>
              <option value="inflow">Inflow</option>
              <option value="internal_outflow">Internal outflow</option>
              <option value="internal_inflow">Internal inflow</option>
              <option value="outflow">Outflow</option>
            </Select>
          </FilterField>
          <FilterField label="Movement">
            <Select
              value={movementType}
              onChange={(e) => {
                setMovementType(e.target.value)
                setPage(1)
              }}
            >
              <option value="">All movements</option>
              {[
                "OPENING",
                "PURCHASE",
                "PURCHASE_MARKET",
                "RECEIVE",
                "ISSUE",
                "RETURN",
                "CONSUMPTION",
                "SOLD",
                "DISCARD",
                "LOST",
                "ADJUSTMENT",
              ].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField label="From" className="lg:w-40">
            <TextInput
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value)
                setPage(1)
              }}
            />
          </FilterField>
          <FilterField label="To" className="lg:w-40">
            <TextInput
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value)
                setPage(1)
              }}
            />
          </FilterField>
        </FilterRow>
      }
      footer={
        pagination ? (
          <Pagination
            pagination={pagination}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l)
              setPage(1)
            }}
          />
        ) : null
      }
    >
      <DataTable
        colSpan={10}
        loading={loading}
        empty={rows.length === 0}
        loadingText="Loading movements..."
        emptyText="No movements found"
        emptyHint={<ScopeNote />}
        emptyIcon={<FaStream />}
        head={
          <>
            <Th>When</Th>
            <Th>Item</Th>
            <Th>Holder</Th>
            <Th>Counterparty</Th>
            <Th>Movement</Th>
            <Th>Flow</Th>
            <Th className="text-right">In</Th>
            <Th className="text-right">Out</Th>
            <Th>Reference</Th>
            <Th>By</Th>
          </>
        }
      >
        {rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-50 transition-colors">
            <Cell className="text-gray-500">{dateTimeText(row.created_at)}</Cell>
            <Cell>
              {row.item?.item_name || `#${row.item_id}`}
              {row.item?.item_code ? <span className="text-xs text-gray-400"> ({row.item.item_code})</span> : null}
            </Cell>
            <Cell>
              <HolderChip type={row.holder_type} label={row.holder_label} code={row.holder_code} />
            </Cell>
            <Cell>
              {row.counterparty_holder_type ? (
                <HolderChip
                  type={row.counterparty_holder_type}
                  label={row.counterparty_label}
                  code={row.counterparty_code}
                />
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </Cell>
            <Cell className="text-xs">{row.movement_type}</Cell>
            <Cell>
              <span
                className={`inline-flex px-2 py-0.5 text-[11px] rounded ${
                  FLOW_TONE[row.flow_category] || "bg-gray-100 text-gray-700"
                }`}
              >
                {FLOW_LABEL[row.flow_category] || row.flow_category}
              </span>
            </Cell>
            <Cell className="text-right text-green-700">{Number(row.quantity_in) ? qty(row.quantity_in) : "-"}</Cell>
            <Cell className="text-right text-red-700">{Number(row.quantity_out) ? qty(row.quantity_out) : "-"}</Cell>
            <Cell className="text-xs text-gray-500">
              {row.reference_type ? `${row.reference_type} #${row.reference_id}` : row.remarks || "-"}
            </Cell>
            <Cell className="text-xs">{fullName(row.creator)}</Cell>
          </tr>
        ))}
      </DataTable>
    </ListCard>
  )
}

export default MaterialStockPage
